import { Gender, Job } from 'graphql/generated';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { getDateText, formatDate } from 'utils/dateTimeHelper';
import { DriverLisences } from 'views/job-detail/components/lisence-types';
import { Skills } from 'views/job-detail/components/skills';
import { Summary } from '../summary';
import { JobSummaryItem } from './job-summary-item';
import PersonIcon from '@material-ui/icons/Person';
import SchoolIcon from '@material-ui/icons/School';
import CardTravelIcon from '@material-ui/icons/CardTravel';
import QueryBuilderIcon from '@material-ui/icons/QueryBuilder';
import WcIcon from '@material-ui/icons/Wc';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import BusinessIcon from '@material-ui/icons/Business';
import AccessibleIcon from '@material-ui/icons/Accessible';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import i18n from 'i18n';

const getAge = ({ ageMin, ageMax }) => {
    if (ageMin && ageMax) {
        return i18n.t('jobDetails:ageRange', { AgeMin: ageMin, count: ageMax });
    }

    if (ageMin) {
        return i18n.t('jobDetails:ageMin', { count: ageMin });
    }

    return null;
};

type JobSummaryProps = {
    job: Job;
};

export const JobSummary: React.FC<JobSummaryProps> = ({ job }) => {
    const {
        education,
        employment,
        experience,
        gender,
        publishedAt,
        expiresAt,
        age,
        driverLicenseTypes,
        acceptHandicapped,
        acceptIncompleteResumes,
        regions,
        keySkills,
    } = job;

    const { t } = useTranslation();
    const Age = age && getAge({ ageMin: age.from, ageMax: age.to });
    const Experience = experience ? t(`enum:experience_${experience}`) : undefined;
    const Education = education ? t(`enum:education_${education}`) : undefined;
    const Employment = employment ? t(`enum:employment_${employment}`) : undefined;
    const skills = keySkills || [];
    const region = regions.edges.map(({ node }) => node.name);

    return (
        <Summary title={t('jobDetails:jobSummary')}>
            {Age && <JobSummaryItem title={t('jobDetails:age')} text={Age} icon={<PersonIcon />} />}
            {Education && <JobSummaryItem title={t('jobDetails:education')} text={Education} icon={<SchoolIcon />} />}
            {Experience && (
                <JobSummaryItem title={t('jobDetails:experience')} text={Experience} icon={<CardTravelIcon />} />
            )}
            {Employment && (
                <JobSummaryItem title={t('jobDetails:employment')} text={Employment} icon={<QueryBuilderIcon />} />
            )}
            {gender && gender !== Gender.Unknown && (
                <JobSummaryItem title={t('jobDetails:gender')} text={t(`enum:gender_${gender}`)} icon={<WcIcon />} />
            )}
            {publishedAt && (
                <JobSummaryItem
                    title={t('jobDetails:posted')}
                    text={getDateText(publishedAt)}
                    icon={<CalendarTodayIcon />}
                />
            )}
            {expiresAt && (
                <JobSummaryItem
                    title={t('jobDetails:expireDate')}
                    text={formatDate(expiresAt)}
                    icon={<CalendarTodayIcon />}
                />
            )}

            {driverLicenseTypes && <DriverLisences types={driverLicenseTypes} />}

            {region && (
                <JobSummaryItem title={t('jobDetails:location')} text={t(`region:${region}`)} icon={<BusinessIcon />} />
            )}
            {acceptHandicapped && (
                <JobSummaryItem title={''} text={t('jobDetails:acceptHandicapped')} icon={<AccessibleIcon />} />
            )}
            {acceptIncompleteResumes && (
                <JobSummaryItem title={''} text={t('jobDetails:acceptIncompleteResumes')} icon={<AttachFileIcon />} />
            )}

            {!!skills?.length && <Skills {...{ skills }} />}
        </Summary>
    );
};
